import { useEffect, useMemo, useState } from 'react';

import {
  AssetColumnId,
  BULK_PDF_EXPORT_LIMIT,
  GetCheckResponseDTO,
  GetIssuesResponseDTO,
  GetWorkOrdersRequestDTO,
  GetWorkOrdersResponseDTO,
  MODULE,
  PDF_SCOPE,
  PdfScope,
  RecordState,
  WorkOrderStatus,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { useLocation } from 'react-router-dom';

import { ArchiveConfirmationDialog } from '@/components/common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '@/components/common/delete-confirmation-dialog';
import { RestoreConfirmationDialog } from '@/components/common/restore-confirmation-dialog';
import { ShareDialog } from '@/components/common/share-dialog';
import { FeedbackPrompt } from '@/components/feedback/feedback-prompt';
import ArchiveIcon from '@/components/icons/archive';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import FolderOpenIcon from '@/components/icons/folder-open';
import GearIcon from '@/components/icons/gear';
import TagIcon from '@/components/icons/tag';
import UndoIcon from '@/components/icons/undo';
import ColumnsDialog from '@/components/table-page/columns-dialog';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton, TablePageTableGroupDropdown } from '@/components/table-page/table-page-header';
import TablePageSearchBar from '@/components/table-page/table-page-search-bar';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { TablePageTitle } from '@/components/table-page/table-page-title';
import { ButtonStateToggle } from '@/components/ui/button-state-toggle';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import WorkOrderDrawer from '@/components/work-orders/work-order-drawer';
import { useFeedbackPrompt } from '@/hooks/use-feedback-prompt';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import LinkViewIcon from '@/legacy/components/icons/link-view';
import ShareIcon from '@/legacy/components/icons/share';
import { tagKeys } from '@/queries/assets';
import { buildFilterFields } from '@/queries/column-filter';
import { awaWorkOrderKeys, getWorkOrdersInfiniteQueryOptions, workOrderKeys } from '@/queries/work-orders';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';
import { getShareTargets } from '@/utils/share-targets';

import { BulkUpdateWorkOrderTagsDialog } from './bulk-update-work-order-tags-dialog';
import { NoWorkOrdersCreatedDisplay, NoWorkOrdersFoundFromFiltersDisplay, NoWorkOrdersFoundFromSearchDisplay, WorkOrdersTable } from './table';
import { columns, FILTER_FIELD_MAP, groupOptions } from './table-meta';
import { UpdateWorkOrderStatusDialog } from './update-work-order-status-dialog';

function WorkOrdersPage() {
  const queryClient = useQueryClient();
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const toast = useToast();

  const { isAdmin, canExportCsv } = usePermissions();
  const { triggerForWorkOrder, promptProps: feedbackPromptProps } = useFeedbackPrompt('work-orders');
  const locationState = useLocation().state as
    | {
        workOrder?: GetWorkOrdersResponseDTO['data'][number];
        issue?: GetIssuesResponseDTO['data'][number];
        check?: GetCheckResponseDTO;
      }
    | undefined;
  const workOrderFromLocation = locationState?.workOrder;
  const issueFromLocation = locationState?.issue;
  const checkFromLocation = locationState?.check;

  const { setModuleFilters } = useModulesStore();

  const {
    config: { columnOrder, grouping, sorting, filters, columnVisibility },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.WorkOrders, {
    filterFieldMap: FILTER_FIELD_MAP,
    groupOptions,
    columns,
  });

  const [openWorkOrderDrawer, setOpenWorkOrderDrawer] = useState(!!workOrderFromLocation || !!issueFromLocation || !!checkFromLocation);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<GetWorkOrdersResponseDTO['data'][number] | null>(
    workOrderFromLocation || issueFromLocation?.workOrder || null,
  );
  const [selectedIssue, setSelectedIssue] = useState<GetIssuesResponseDTO['data'][number] | null>(issueFromLocation || null);
  const [selectedCheck, setSelectedCheck] = useState<GetCheckResponseDTO | null>(checkFromLocation || null);
  const [openColumnsDialog, setOpenColumnsDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [openStatusUpdate, setOpenStatusUpdate] = useState(false);
  const [openWorkOrderTagsUpdate, setOpenWorkOrderTagsUpdate] = useState(false);
  const [search, setSearch] = useState('');
  const [workOrderState, setWorkOrderState] = useState<RecordState>(RecordState.Active);
  const [openArchiveConfirmation, setOpenArchiveConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openRestoreConfirmation, setOpenRestoreConfirmation] = useState(false);

  useEffect(() => {
    if (locationState) {
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [shareUrl, setShareUrl] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: workOrdersData,
    fetchNextPage: fetchNextWorkOrdersPage,
    hasNextPage: hasNextWorkOrdersPage,
    isFetching: isFetchingWorkOrders,
    isFetchingNextPage: isFetchingNextWorkOrdersPage,
    error: workOrdersError,
  } = useInfiniteQuery(
    getWorkOrdersInfiniteQueryOptions({
      search: debouncedSearch,
      state: workOrderState,
      filters: buildFilterFields(filters, FILTER_FIELD_MAP, {
        transformValue: (id, raw) => (id === 'status' ? (raw ?? [WorkOrderStatus.Open, WorkOrderStatus.Closed]) : raw),
      }),
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetWorkOrdersRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetWorkOrdersRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedWorkOrders = useMemo(() => {
    return (incomingData: typeof workOrdersData) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const assets = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => assets[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedWorkOrders = useMemo(() => getSelectedWorkOrders(workOrdersData), [workOrdersData, getSelectedWorkOrders]);

  const { mutateAsync: bulkUpdateWorkOrderStatus, isPending: isUpdatingStatus } = useMutation({
    mutationFn: async (status: WorkOrderStatus) => {
      const ids = selectedWorkOrders.map((wo) => wo.id);
      return ApiService.BulkUpdateWorkOrderStatus({ ids, status });
    },
    onSuccess: (_, status) => {
      toast.success('Work orders updated successfully');
      setOpenStatusUpdate(false);
      const closedWorkOrders = status === WorkOrderStatus.Closed ? [...selectedWorkOrders] : [];
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
      if (closedWorkOrders.length > 0) {
        // Attribute the prompt to the first selected WO — the rating is about the close-out experience, not a specific entity.
        triggerForWorkOrder(closedWorkOrders[0]);
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { mutateAsync: bulkUpdateWorkOrderTags, isPending: isUpdatingWorkOrderTags } = useMutation({
    mutationFn: async (tags: string[]) => {
      const ids = selectedWorkOrders.map((wo) => wo.id);
      return ApiService.BulkUpdateWorkOrderTags({ workOrderIds: ids, tags });
    },
    onSuccess: () => {
      toast.success('Work orders updated successfully');
      setOpenWorkOrderTagsUpdate(false);
      setSelectedRows({});
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: tagKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleUpdateWorkOrderTags = async (tags: string[], resetForm: () => void) => {
    await bulkUpdateWorkOrderTags(tags);
    resetForm();
  };

  const { mutateAsync: bulkUpdateWorkOrderState, isPending: isUpdatingWorkOrderState } = useMutation({
    mutationFn: async ({ ids, state }: { ids: string[]; state: RecordState }) => await ApiService.BulkUpdateWorkOrderState({ ids, state }),
    onSuccess: (_, { state }) => {
      const messages = {
        [RecordState.Active]: 'Work orders restored successfully',
        [RecordState.Archived]: 'Work orders archived successfully',
        [RecordState.Deleted]: 'Work orders deleted successfully',
      };
      toast.success(messages[state]);
      setOpenArchiveConfirmation(false);
      setOpenDeleteConfirmation(false);
      setOpenRestoreConfirmation(false);
      setSelectedRows({});

      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: awaWorkOrderKeys.all });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleBulkArchive() {
    bulkUpdateWorkOrderState({ ids: selectedWorkOrders.map((wo) => wo.id), state: RecordState.Archived });
  }

  function handleBulkDelete() {
    bulkUpdateWorkOrderState({ ids: selectedWorkOrders.map((wo) => wo.id), state: RecordState.Deleted });
  }

  function handleBulkRestore() {
    bulkUpdateWorkOrderState({ ids: selectedWorkOrders.map((wo) => wo.id), state: RecordState.Active });
  }

  const { shareUrl: shareTarget, appUrl } = getShareTargets('work-orders');

  function buildWorkOrdersExportRequest(): RegisterExportQuery<'work-orders'> {
    const selected = getSelectedWorkOrders(workOrdersData);
    return {
      queryType: 'work-orders',
      filters: { state: workOrderState, fields: { id: selected.map((workOrder) => workOrder.id) } },
      groupBy: grouping.length
        ? { field: grouping[0] as NonNullable<GetWorkOrdersRequestDTO['groupBy']>['field'], direction: (sorting.direction || 'asc') as 'asc' | 'desc' }
        : undefined,
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetWorkOrdersRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!workOrdersData?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildWorkOrdersExportRequest(), filename: buildCsvExportFilename('work-orders') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!workOrdersData?.pages) return;
    const selectedWorkOrders = getSelectedWorkOrders(workOrdersData);
    const job = buildPdfExportJob({ scope, recordType: 'work-order', records: selectedWorkOrders, register: buildWorkOrdersExportRequest() });
    // Warn before the API rejects an over-limit set. The attached-records scope
    // expands each work order into a manifest (issues + their repairs, checks +
    // their schedule-forms), so guard on the manifest the job actually carries —
    // deriving the count from the same builder keeps it from drifting. The other
    // record-carrying scopes export one record per work order.
    const manifestRecordCount = job.kind === 'pdf-scoped' && 'records' in job.request ? job.request.records.length : selectedWorkOrders.length;
    if (scope === PDF_SCOPE.REGISTER_WORK_ORDERS_AND_ATTACHED && manifestRecordCount > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`Select fewer work orders — this scope exports up to ${BULK_PDF_EXPORT_LIMIT} combined work order and attached records.`);
      return;
    }
    if ((scope === PDF_SCOPE.RECORDS || scope === PDF_SCOPE.REGISTER_AND_RECORDS) && selectedWorkOrders.length > BULK_PDF_EXPORT_LIMIT) {
      toast.error(`You can export up to ${BULK_PDF_EXPORT_LIMIT} records at a time. Deselect some and try again.`);
      return;
    }
    openExportTab(job);
  }

  useEffect(() => {
    if (workOrdersError) {
      toast.error(workOrdersError.message);
    }
  }, [toast, workOrdersError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, filters, sorting.key, sorting.direction, grouping]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextWorkOrdersPage,
    hasNextPage: hasNextWorkOrdersPage,
    isFetching: isFetchingWorkOrders,
    isError: !!workOrdersError,
    root: scrollRoot,
  });

  const flattenedData = useMemo(() => workOrdersData?.pages.flatMap((page) => page.data) ?? [], [workOrdersData]);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar h-full w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            {!selectedWorkOrders.length && (
              <>
                <div className="flex h-full items-center gap-x-2 px-2">
                  <TablePageTitle>Work Orders</TablePageTitle>
                  <ButtonStateToggle
                    options={[
                      { icon: <FolderOpenIcon />, label: 'Active', value: RecordState.Active },
                      { icon: <ArchiveIcon />, label: 'Archived', value: RecordState.Archived },
                    ]}
                    value={workOrderState}
                    onChange={(value) => setWorkOrderState(value as RecordState)}
                    className="font-medium"
                    responsive
                  />
                  <TablePageSearchBar search={search} setSearch={setSearch} />
                  {filters && filters.length > 0 && (
                    <TablePageHeaderActionButton
                      icon={<UndoIcon />}
                      label="Remove all Filters"
                      kind="secondary"
                      onClick={() => setModuleFilters(MODULE.WorkOrders, [])}
                    />
                  )}
                </div>

                <div className="mr-1 flex h-full items-center">
                  <div className="flex items-center">
                    <TablePageTableGroupDropdown
                      columns={groupOptions}
                      value={grouping.length ? grouping[0] : ''}
                      onChange={(value) =>
                        updatePageConfig({
                          table: { grouping: value ? [value] : [] },
                        })
                      }
                    />
                    <ColumnsDialog<GetWorkOrdersResponseDTO['data'][number]>
                      open={openColumnsDialog}
                      defaultSectionLabel="Work Order"
                      onOpenChange={setOpenColumnsDialog}
                      columns={columns}
                      columnOrder={columnOrder}
                      onColumnOrderChange={(columnOrder) =>
                        updatePageConfig({
                          table: { columnOrder: columnOrder as Array<{ id: AssetColumnId; active: boolean }> },
                        })
                      }
                    />
                  </div>
                  {shareTarget && <TablePageHeaderActionButton onClick={() => setShareUrl(shareTarget)} icon={<ShareIcon />} label="Share Link" />}
                  {appUrl && <TablePageHeaderActionButton onClick={() => window.open(appUrl, '_blank')} icon={<LinkViewIcon />} label="Open Mechanic App" />}
                  <WorkOrderDrawer
                    open={openWorkOrderDrawer}
                    onOpenChange={(open) => {
                      setOpenWorkOrderDrawer(open);
                      if (!open) {
                        setSelectedWorkOrder(null);
                        setSelectedIssue(null);
                        setSelectedCheck(null);
                      }
                    }}
                    workOrderId={selectedWorkOrder?.id}
                    issue={selectedIssue}
                    check={selectedCheck}
                    registerState={workOrderState}
                    onCloseSuccess={triggerForWorkOrder}
                  />
                </div>
              </>
            )}
            {selectedWorkOrders.length > 0 && (
              <div className="flex min-h-10 items-center gap-1 px-2">
                <TablePageSelectedCountIndicator selectedCount={selectedWorkOrders.length} totalCount={workOrdersData?.pages[0]?.totalCount ?? 0} />
                {canExportCsv && <TablePageHeaderActionButton icon={<DownloadCsvIcon />} label="Download CSV" onClick={handleCsvExport} />}
                <DownloadPdfMenu
                  recordNoun="work order"
                  extraItems={[
                    {
                      scope: PDF_SCOPE.REGISTER_WORK_ORDERS_AND_ATTACHED,
                      label: 'Register, work order records and attached records',
                      help: 'Download the summary sheet, then each selected work order immediately followed by its linked issue and check records (and their repair and schedule-form records), bound into one PDF.',
                    },
                  ]}
                  onSelect={handlePdfScopeSelect}
                  canExportRegister={canExportCsv}
                />
                <TablePageHeaderActionButton icon={<GearIcon />} label="Edit Status" onClick={() => setOpenStatusUpdate(true)} />
                <TablePageHeaderActionButton icon={<TagIcon />} label="Edit Tags" onClick={() => setOpenWorkOrderTagsUpdate(true)} />
                {isAdmin && workOrderState === RecordState.Active && (
                  <TablePageHeaderActionButton icon={<ArchiveIcon />} label="Archive Selected" onClick={() => setOpenArchiveConfirmation(true)} />
                )}
                {isAdmin && workOrderState === RecordState.Archived && (
                  <>
                    <TablePageHeaderActionButton icon={<UndoIcon />} label="Restore Selected" onClick={() => setOpenRestoreConfirmation(true)} />
                    <TablePageHeaderActionButton
                      icon={<DeleteIcon className="text-default-red" />}
                      label="Delete Selected"
                      onClick={() => setOpenDeleteConfirmation(true)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <WorkOrdersTable
          data={flattenedData}
          loading={(isFetchingWorkOrders && !isFetchingNextWorkOrdersPage) || isLoadingPageConfig}
          notFoundDisplay={
            debouncedSearch.length > 0 ? (
              <NoWorkOrdersFoundFromSearchDisplay />
            ) : filters && filters.length > 0 ? (
              <NoWorkOrdersFoundFromFiltersDisplay
                openWorkOrderDrawer={() => setOpenWorkOrderDrawer(true)}
                removeColumnFilters={() => setModuleFilters(MODULE.WorkOrders, [])}
              />
            ) : (
              <NoWorkOrdersCreatedDisplay openWorkOrderDrawer={() => setOpenWorkOrderDrawer(true)} />
            )
          }
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.WorkOrders, filters)}
          sorting={sorting}
          grouping={grouping}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          onRowClick={(row) => {
            setSelectedWorkOrder(row);
            setOpenWorkOrderDrawer(true);
          }}
          onSortingChange={(newSorting) =>
            updatePageConfig({
              table: { sorting: newSorting },
            })
          }
        />
        {hasNextWorkOrdersPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextWorkOrdersPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>

      {shareUrl && <ShareDialog url={shareUrl} onOpenChange={() => setShareUrl(null)} open={!!shareUrl} />}

      <UpdateWorkOrderStatusDialog open={openStatusUpdate} onOpenChange={setOpenStatusUpdate} callback={bulkUpdateWorkOrderStatus} loading={isUpdatingStatus} />

      <BulkUpdateWorkOrderTagsDialog
        open={openWorkOrderTagsUpdate}
        onOpenChange={setOpenWorkOrderTagsUpdate}
        callback={handleUpdateWorkOrderTags}
        loading={isUpdatingWorkOrderTags}
      />
      <ArchiveConfirmationDialog
        open={openArchiveConfirmation}
        onOpenChange={setOpenArchiveConfirmation}
        callback={handleBulkArchive}
        loading={isUpdatingWorkOrderState}
        title="Archive work orders?"
        description="Are you sure you want to archive the selected work orders? Open tasks will be removed. You can restore them later from the archived tab."
      />
      <DeleteConfirmationDialog
        open={openDeleteConfirmation}
        onOpenChange={setOpenDeleteConfirmation}
        callback={handleBulkDelete}
        loading={isUpdatingWorkOrderState}
        title="Delete work orders?"
        description="Are you sure you want to delete the selected work orders? This action cannot be undone."
      />
      <RestoreConfirmationDialog
        open={openRestoreConfirmation}
        onOpenChange={setOpenRestoreConfirmation}
        callback={handleBulkRestore}
        loading={isUpdatingWorkOrderState}
        title="Restore work orders?"
        description="Are you sure you want to restore the selected work orders? They will be moved back to the active tab."
      />

      <FeedbackPrompt {...feedbackPromptProps} />
    </div>
  );
}

export default WorkOrdersPage;
